import { useState } from "react";
import { toast } from "sonner";
import { BookOpen, Copy, Check, Terminal } from "lucide-react";
import { API } from "@/lib/api";

const BASE = `${API}/v1`;

const SNIPPETS = {
  python: `from openai import OpenAI

client = OpenAI(
    api_key="crea_votre_cle",
    base_url="${BASE}",   # ← la seule ligne à changer
)

# Streaming
stream = client.chat.completions.create(
    model="crerea-pro",
    messages=[{"role": "user", "content": "Explique CRÉRÉA en une phrase"}],
    stream=True,
)
for chunk in stream:
    print(chunk.choices[0].delta.content or "", end="")`,
  node: `import OpenAI from "openai";

const client = new OpenAI({
  apiKey: "crea_votre_cle",
  baseURL: "${BASE}",   // ← la seule ligne à changer
});

const stream = await client.chat.completions.create({
  model: "crerea-pro",
  messages: [{ role: "user", content: "Explique CRÉRÉA en une phrase" }],
  stream: true,
});
for await (const chunk of stream) {
  process.stdout.write(chunk.choices[0].delta.content || "");
}`,
  curl: `curl ${BASE}/chat/completions \\
  -H "Authorization: Bearer crea_votre_cle" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "crerea-pro",
    "messages": [{"role": "user", "content": "Explique CRÉRÉA en une phrase"}],
    "stream": true
  }'`,
};

const IMAGE_SNIPPET = `curl ${BASE}/images/generations \\
  -H "Authorization: Bearer crea_votre_cle" \\
  -H "Content-Type: application/json" \\
  -d '{"model": "crerea-vision", "prompt": "un renard doré, cinématographique"}'
# → { "data": [{ "url": "https://…/image.png" }] }`;

const VIDEO_SNIPPET = `curl ${BASE}/videos/generations \\
  -H "Authorization: Bearer crea_votre_cle" \\
  -H "Content-Type: application/json" \\
  -d '{"model": "crerea-motion", "prompt": "vague dorée au ralenti"}'
# → { "data": [{ "url": "https://…/video.mp4" }] }`;

const MODELS_SNIPPET = `curl ${BASE}/models -H "Authorization: Bearer crea_votre_cle"`;

function Code({ text, id }) {
  const [copied, setCopied] = useState(false);
  const copy = () => { navigator.clipboard.writeText(text); setCopied(true); toast.success("Copié"); setTimeout(() => setCopied(false), 1500); };
  return (
    <div className="group relative">
      <button onClick={copy} data-testid={`copy-${id}`} className="absolute right-3 top-3 flex items-center gap-1 rounded-md bg-secondary/80 px-2 py-1 text-xs text-muted-foreground transition-colors hover:text-gold-400">
        {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
      </button>
      <pre className="overflow-x-auto rounded-xl border border-border bg-[#070B19] p-4 font-mono text-xs leading-relaxed text-slate-200"><code>{text}</code></pre>
    </div>
  );
}

export default function Docs() {
  const [lang, setLang] = useState("python");
  return (
    <div className="space-y-6" data-testid="docs-page">
      <div>
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-gold-400">Docs API</p>
        <h1 className="mt-1 font-display text-3xl font-bold tracking-tight">Compatible OpenAI — drop-in</h1>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">Gardez votre code. Changez uniquement <code className="font-mono text-gold-400">base_url</code> et utilisez votre clé <code className="font-mono">crea_…</code>. Auth par en-tête <code className="font-mono">Authorization: Bearer</code> ou <code className="font-mono">X-API-Key</code>.</p>
      </div>

      <div className="rounded-2xl border border-border bg-card p-5">
        <div className="mb-2 flex items-center gap-2"><BookOpen className="h-4 w-4 text-gold-400" /><h3 className="font-display font-semibold">Base URL</h3></div>
        <Code id="base" text={BASE} />
      </div>

      <div className="rounded-2xl border border-border bg-card p-5">
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2"><Terminal className="h-4 w-4 text-gold-400" /><h3 className="font-display font-semibold">Chat completions (streaming)</h3></div>
          <div className="flex gap-1.5">
            {["python", "node", "curl"].map((l) => (
              <button key={l} onClick={() => setLang(l)} data-testid={`docs-lang-${l}`}
                className={`rounded-md px-3 py-1 font-mono text-xs transition-colors ${lang === l ? "bg-gold-500/15 text-gold-400" : "text-muted-foreground hover:text-foreground"}`}>
                {l === "node" ? "Node.js" : l === "curl" ? "cURL" : "Python"}
              </button>
            ))}
          </div>
        </div>
        <Code id="chat" text={SNIPPETS[lang]} />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-2xl border border-border bg-card p-5">
          <h3 className="mb-2 font-display font-semibold">Génération d'image</h3>
          <Code id="image" text={IMAGE_SNIPPET} />
        </div>
        <div className="rounded-2xl border border-border bg-card p-5">
          <h3 className="mb-2 font-display font-semibold">Génération de vidéo</h3>
          <Code id="video" text={VIDEO_SNIPPET} />
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-card p-5">
        <h3 className="mb-2 font-display font-semibold">Lister les modèles</h3>
        <Code id="models" text={MODELS_SNIPPET} />
      </div>

      <div className="rounded-2xl border border-border bg-card p-5 text-sm text-muted-foreground">
        Chaque appel réussi débite votre solde de crédits (selon le modèle). Aucun crédit n'est débité en cas d'échec. Rechargez dans l'onglet Crédits.
      </div>
    </div>
  );
}
